import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon, MapPinIcon, UndoIcon } from 'lucide-react';
import type { QuestionRendererProps } from './types';
import { MapCanvas } from '../../../visuals/components/topoMap/MapCanvas';
import { FALLBACK_COLOR, hexToRgba } from './rendererUtils';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
/**
 * MapPlaceCitiesRenderer — Place an ORDERED list of cities on the map.
 *
 * data: {
 *   mapId: string,
 *   targetPoints: Array<{ name: string, lat: number, lon: number, cityId?: string }>,
 *   toleranceKm: number
 * }
 *
 * Answer payload: JSON.stringify(Array<{ lat: number, lon: number }>)
 * matching the order of data.targetPoints.
 *
 * UX: shows the city list above the map; the active city is highlighted.
 * Click on the map → marker placed as numbered pin → next city becomes active.
 * "Vorige" lets the student step back and re-place a pin. The student
 * confirms the full set with the global "Controleren" button.
 *
 * Submit pattern: emits onAnswer once when shouldValidate flips to true.
 * No internal submit button — only an "Vorige" undo button (which is not a submit).
 *
 * No labels are shown on placed pins to prevent answer leak.
 */
export function MapPlaceCitiesRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate,
  moduleColor
}: QuestionRendererProps) {
  // R-13 fix: all hooks MUST run unconditionally, BEFORE any early returns.
  const color = moduleColor || FALLBACK_COLOR;
  const data = (question.data ?? {}) as Record<string, unknown>;
  const mapId = String(data.mapId ?? '');
  const targets = useMemo(() => {
    const arr = Array.isArray(data.targetPoints) ? data.targetPoints as Array<{
      name: string;
      lat: number;
      lon: number;
      cityId?: string;
    }> : [];
    return arr;
  }, [data.targetPoints]);
  const [placements, setPlacements] = useState<Array<{
    lat: number;
    lon: number;
  } | null>>(() => targets.map(() => null));
  const [currentIndex, setCurrentIndex] = useState(0);
  useEffect(() => {
    setPlacements(targets.map(() => null));
    setCurrentIndex(0);
  }, [question.id, targets]);
  const allPlaced = placements.length > 0 && placements.every(p => p !== null);
  useEffect(() => {
    onReadyChange?.(allPlaced);
  }, [allPlaced, onReadyChange]);
  const answerPayload = useMemo(() => JSON.stringify(placements), [placements]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, onAnswer, question.id);
  // Guard AFTER all hooks have run.
  if (!question.data || !mapId) return null;
  const handlePointSelect = (coord: {
    lat: number;
    lon: number;
  }) => {
    if (currentIndex >= targets.length) return;
    const next = [...placements];
    next[currentIndex] = coord;
    setPlacements(next);
    const nextEmpty = next.findIndex(p => p === null);
    setCurrentIndex(nextEmpty >= 0 ? nextEmpty : targets.length);
  };
  const handleUndo = () => {
    let lastPlaced = -1;
    for (let i = placements.length - 1; i >= 0; i -= 1) {
      if (placements[i] !== null) {
        lastPlaced = i;
        break;
      }
    }
    if (lastPlaced < 0) return;
    const next = [...placements];
    next[lastPlaced] = null;
    setPlacements(next);
    setCurrentIndex(lastPlaced);
  };
  const placedMarkers = placements.map((p, i) => p ? {
    lat: p.lat,
    lon: p.lon,
    index: i + 1,
    isActive: false
  } : null).filter((m): m is {
    lat: number;
    lon: number;
    index: number;
    isActive: boolean;
  } => m !== null);
  const activeName = currentIndex < targets.length ? targets[currentIndex]?.name : null;
  return <div className="flex flex-col items-center gap-4 w-full" data-id="element-10613">
      {/* City list / progress */}
      <div className="w-full max-w-md" data-id="element-10614">
        <div className="flex items-center justify-between mb-2" data-id="element-10615">
          <span className="text-sm text-stone-500 flex items-center gap-1.5" data-id="element-10616">
            <MapPinIcon className="w-4 h-4" data-id="element-10617" />
            Plaats deze steden op de kaart
          </span>
          <span className="text-xs text-stone-500 font-medium" data-id="element-10618">
            {placements.filter(p => p !== null).length} / {targets.length}
          </span>
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-10619">
          {targets.map((t, i) => {
          const placed = placements[i] !== null;
          const isActive = i === currentIndex && !placed;
          return <motion.div key={t.cityId ?? `${t.name}-${i}`} className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-medium border-2" style={{
            backgroundColor: isActive ? hexToRgba(color, 0.12) : placed ? '#F0FDF4' : '#FFFEF8',
            borderColor: isActive ? color : placed ? '#86EFAC' : '#E7E2D6',
            color: isActive ? color : placed ? '#15803D' : '#78716C'
          }} animate={isActive ? {
            scale: [1, 1.04, 1]
          } : {
            scale: 1
          }} transition={{
            duration: 1.4,
            repeat: isActive ? Infinity : 0,
            ease: 'easeInOut'
          }} data-id="element-10620">
                <span className="w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold" style={{
              backgroundColor: placed ? '#22C55E' : isActive ? color : '#D6D3D1',
              color: '#FFFEF8'
            }} data-id="element-10621">
                  {placed ? <CheckIcon className="w-3 h-3" data-id="element-10622" /> : i + 1}
                </span>
                {t.name}
              </motion.div>;
        })}
        </div>
        <AnimatePresence mode="wait" data-id="element-10623">
          {activeName && <motion.p key={activeName} initial={{
          opacity: 0,
          y: -4
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: 4
        }} className="text-sm mt-3 font-semibold" style={{
          color
        }} data-id="element-10624">
              👉 Klik nu waar <span className="underline" data-id="element-10625">{activeName}</span>{' '}
              ligt
            </motion.p>}
          {!activeName && allPlaced && <motion.p key="done" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="text-sm mt-3 font-semibold text-emerald-700" data-id="element-10626">
              ✅ Alle steden geplaatst — klik op Controleren.
            </motion.p>}
        </AnimatePresence>
      </div>

      <MapCanvas mapId={mapId} mode="select-point" onPointSelect={handlePointSelect} placedMarkers={placedMarkers} width={420} height={420} ariaLabel={question.question} moduleColor={color} data-id="element-10627" />

      {placements.some(p => p !== null) && <motion.button type="button" onClick={handleUndo} className="px-4 py-2 rounded-2xl font-medium text-stone-700 bg-stone-100 hover:bg-stone-200 flex items-center gap-2 transition-colors text-sm" whileTap={{
      scale: 0.97
    }} aria-label="Laatste plaatsing terugnemen" data-id="element-10628">
          <UndoIcon className="w-4 h-4" data-id="element-10629" />
          Vorige stip terugnemen
        </motion.button>}
    </div>;
}